import { create } from 'zustand';
import type { ICommand } from '../../../core/commands/ICommand';
import type { OrgPlanningReviewScope } from '../hooks/orgPlanningReviewKey';
import { isOrgPlanningReviewScopeCurrent } from '../hooks/useOrgPlanningReviewScope';
import { subscribeProtectedPlanningInvalidation } from '../hooks/protectedPlanningGeneration';
import { atlasReviewBlocksEditing } from './atlasChangesStore';
import { sameOrgPlanningReviewScope } from '../hooks/orgPlanningReviewKey';
import { PositionCommandSettlementError } from '../hooks/positionCommandSettlement';

interface SaveAttempt {
  readonly id: string;
  readonly scope: OrgPlanningReviewScope;
  readonly phase: 'saving' | 'failed' | 'unknown';
  readonly groupKey?: string;
  readonly outcomeUnknown?: boolean;
  readonly historyAction?: 'undo' | 'redo';
}

// Closures retain unsuccessful command input in session memory only. They are
// never serialized into Zustand persistence, query keys, URLs, or telemetry.
const retryCommands = new Map<string, ICommand>();
const discardInputs = new Map<string, () => void>();
interface CanvasRefresh {
  readonly scope: OrgPlanningReviewScope;
  readonly headChangeSetId: string;
}

export const useAtlasDraftSaveRecoveryStore = create<{
  attempts: Readonly<Record<string, SaveAttempt>>;
  canvasRefreshes: Readonly<Record<string, CanvasRefresh>>;
  statusOwners: Readonly<Record<string, number>>;
}>(() => ({ attempts: {}, canvasRefreshes: {}, statusOwners: {} }));

export const atlasDraftCanvasRefreshKey = (folderId: string, diagramId: string) => `${folderId}:${diagramId}`;

/** A surface that narrates "Saving draft…" itself, so the canvas fallback stays quiet. Returns the release. */
export function claimAtlasDraftSaveStatus(folderId: string, diagramId: string): () => void {
  const key = atlasDraftCanvasRefreshKey(folderId, diagramId);
  useAtlasDraftSaveRecoveryStore.setState((state) => ({
    statusOwners: { ...state.statusOwners, [key]: (state.statusOwners[key] ?? 0) + 1 },
  }));
  return () => useAtlasDraftSaveRecoveryStore.setState((state) => {
    const statusOwners = { ...state.statusOwners };
    const remaining = (statusOwners[key] ?? 1) - 1;
    if (remaining > 0) statusOwners[key] = remaining;
    else delete statusOwners[key];
    return { statusOwners };
  });
}

/** Display-only: an accepted head stays "saving" until the canvas has its projection. Never blocks history or navigation. */
export function trackAtlasDraftCanvasRefresh(scope: OrgPlanningReviewScope, headChangeSetId: string): void {
  if (!isOrgPlanningReviewScopeCurrent(scope)) return;
  const key = atlasDraftCanvasRefreshKey(scope.folderId, scope.diagramId);
  useAtlasDraftSaveRecoveryStore.setState((state) => ({
    canvasRefreshes: { ...state.canvasRefreshes, [key]: { scope, headChangeSetId } },
  }));
}

export function completeAtlasDraftCanvasRefresh(folderId: string, diagramId: string, headChangeSetId?: string): void {
  const key = atlasDraftCanvasRefreshKey(folderId, diagramId);
  useAtlasDraftSaveRecoveryStore.setState((state) => {
    const current = state.canvasRefreshes[key];
    if (!current || (headChangeSetId && current.headChangeSetId !== headChangeSetId)) return state;
    const canvasRefreshes = { ...state.canvasRefreshes };
    delete canvasRefreshes[key];
    return { canvasRefreshes };
  });
}

export function assertAtlasDraftHistoryAllowed(scope: OrgPlanningReviewScope, ownAttemptId: string): void {
  if (!isOrgPlanningReviewScopeCurrent(scope)) throw new PositionCommandSettlementError('http_error', 403, 'authorization_denied');
  if (atlasReviewBlocksEditing(scope)) throw new Error('Return to editing from the pending review before changing draft history.');
  if (Object.values(useAtlasDraftSaveRecoveryStore.getState().attempts).some((attempt) =>
    attempt.id !== ownAttemptId && sameOrgPlanningReviewScope(attempt.scope, scope))) {
    throw new Error('Resolve the earlier unsaved edit before changing draft history.');
  }
}

export function trackAtlasDraftSave(id: string, scope: OrgPlanningReviewScope, command: ICommand, phase: SaveAttempt['phase'], groupKey?: string, discardInput?: () => void, historyAction?: 'undo' | 'redo'): void {
  if (!isOrgPlanningReviewScopeCurrent(scope)) return;
  retryCommands.set(id, command);
  if (discardInput) discardInputs.set(id, discardInput);
  useAtlasDraftSaveRecoveryStore.setState((state) => {
    // A later rejected request cannot disprove an earlier lost success.
    // Keep this through the intervening saving phase until an exact receipt.
    const outcomeUnknown = phase === 'unknown' || state.attempts[id]?.outcomeUnknown === true;
    return { attempts: { ...state.attempts, [id]: {
      id, scope, phase: phase === 'failed' && outcomeUnknown ? 'unknown' : phase,
      groupKey, outcomeUnknown, historyAction,
    } } };
  });
}

export function completeAtlasDraftSave(id: string): void {
  retryCommands.delete(id);
  discardInputs.delete(id);
  useAtlasDraftSaveRecoveryStore.setState((state) => {
    const attempts = { ...state.attempts };
    delete attempts[id];
    return { attempts };
  });
}

export function recoverAtlasDraftSave(id: string): ICommand | undefined {
  const attempt = useAtlasDraftSaveRecoveryStore.getState().attempts[id];
  if (!attempt || attempt.phase === 'saving' || !isOrgPlanningReviewScopeCurrent(attempt.scope)) return undefined;
  return retryCommands.get(id);
}

/** Only a definite rejection can be abandoned without reconciling a receipt. */
export function abandonAtlasDraftSave(id: string): void {
  const attempt = useAtlasDraftSaveRecoveryStore.getState().attempts[id];
  if (attempt?.phase === 'failed' && isOrgPlanningReviewScopeCurrent(attempt.scope)) {
    discardInputs.get(id)?.();
    completeAtlasDraftSave(id);
  }
}

subscribeProtectedPlanningInvalidation((folderId) => {
  for (const attempt of Object.values(useAtlasDraftSaveRecoveryStore.getState().attempts)) {
    if (!folderId || attempt.scope.folderId === folderId) completeAtlasDraftSave(attempt.id);
  }
  for (const refresh of Object.values(useAtlasDraftSaveRecoveryStore.getState().canvasRefreshes)) {
    if (!folderId || refresh.scope.folderId === folderId) {
      completeAtlasDraftCanvasRefresh(refresh.scope.folderId, refresh.scope.diagramId);
    }
  }
});
